// "Where this file is and why" — the two-axis Journey banner at the top of every customer file (Phase 4, Dean 13 Oct 2026).
// 13 Oct (noise pass): the route chip lives here as "Next stage: … → then the next calendar item" — the calendar drives the file.
import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, PauseCircle, PlayCircle, GitBranch, RefreshCw, CalendarClock } from "lucide-react";
import { getCaseJourney } from "@/lib/api";
import { JourneyChips } from "./JourneyLine";
import { HandToLegalButton, PauseLegalDialog, RestructureDecisionDialog } from "./JourneyActions";
import RouteStatusControl from "@/pages/legal/RouteStatusControl";
import { LivePill } from "@/pages/legal/LivePill";

const fmt = (iso) => (iso ? new Date(iso.length > 10 ? iso : iso + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }) : "");
const TONE = { red: "border-rose-200 bg-rose-50/60", amber: "border-amber-200 bg-amber-50/60", emerald: "border-emerald-200 bg-emerald-50/60", slate: "border-slate-200 bg-white" };

function Fact({ label, children, testid }) {
  return (
    <div className="min-w-0" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-xs text-slate-800 leading-snug">{children}</div>
    </div>
  );
}

export function JourneyBanner({ caseRow, onChanged, isLegalMode = false }) {
  const [j, setJ] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);   // pause | restructure
  const load = useCallback(() => {
    setLoading(true);
    getCaseJourney(caseRow.case_id).then(setJ).catch(() => setJ(null)).finally(() => setLoading(false));
  }, [caseRow.case_id]);
  useEffect(() => { load(); }, [load, caseRow.updated_at]);
  if (loading && !j) return <div className="border rounded-lg px-4 py-3 bg-white text-xs text-muted-foreground flex items-center gap-2" data-testid="journey-banner-loading"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Working out where this file is…</div>;
  if (!j) return null;
  const acc = j.account || {}, leg = j.legal || {}, own = j.owners || {}, na = j.next_action, attention = j.attention || [];
  const restructurePending = attention.some((a) => a.key === "restructured_legal_pending");
  const canPause = leg.key === "live", canResume = leg.key === "paused";
  const showRoute = leg.key !== "none" || acc.key === "legal";
  const done = (r) => { setDialog(null); if (r) setJ((prev) => ({ ...(r.journey || r), attention: (r.journey || r).attention || prev?.attention || [] })); onChanged?.(); load(); };
  return (
    <div className={`border rounded-lg px-4 py-3 shadow-sm ${TONE[j.tone] || TONE.slate}`} data-testid="journey-banner">
      <div className="flex flex-wrap items-start gap-x-6 gap-y-2">
        <div className="min-w-[240px]">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Journey</div>
          <div className="flex items-center gap-2 flex-wrap mt-0.5">
            <JourneyChips journey={j} caseId={caseRow.case_id} />
            <span className="text-sm font-semibold text-slate-900" data-testid="journey-banner-line">{j.line}</span>
          </div>
          {leg.detail && leg.key !== "none" && <div className="text-[11px] text-slate-600 mt-0.5" data-testid="journey-banner-legal-detail">{leg.detail}</div>}
        </div>
        <Fact label="Account (LoanDisk)" testid="journey-banner-account">
          {acc.label}{acc.ld_status_id != null ? ` · status ${acc.ld_status_id}` : acc.ld_text ? ` · "${acc.ld_text}"` : " · status unknown"}{acc.dpd ? ` · ${acc.dpd} DPD` : ""}{acc.on_report ? " · on today's report" : ""}
          {acc.conflict && <span className="ml-1 text-amber-700 font-medium">· file says Legal, LoanDisk decides</span>}
        </Fact>
        <Fact label="Lists" testid="journey-banner-lists">
          {[j.on_worklist && "Collections worklist", j.live && "Live legal"].filter(Boolean).join(" · ") || (j.closed ? "Closed — history only" : "Neither")}
          <div className="mt-0.5"><LivePill journey={j} full /></div>
        </Fact>
        <Fact label="Owners" testid="journey-banner-owners">Account {own.account || <span className="italic text-slate-400">unassigned</span>} · Legal {own.legal ? <span className="text-red-800">⚖ {own.legal}</span> : <span className="italic text-slate-400">none</span>}</Fact>
        {j.promise && <Fact label="Promise" testid="journey-banner-promise">{j.promise.label}{j.promise.amount ? ` · £${Number(j.promise.amount).toLocaleString("en-GB")}` : ""}</Fact>}
        <div className="ml-auto flex items-center gap-1.5 flex-wrap">
          <HandToLegalButton caseRow={caseRow} journey={j} onDone={done} />
          {restructurePending && (
            <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] font-semibold border-orange-300 bg-orange-50 text-orange-900 hover:bg-orange-100" onClick={() => setDialog("restructure")} data-testid="journey-restructure-btn">
              <GitBranch className="w-3 h-3 mr-1" /> Legal work still pending?
            </Button>
          )}
          {canPause && <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => setDialog("pause")} data-testid="journey-pause-btn"><PauseCircle className="w-3 h-3 mr-1" /> Pause</Button>}
          {canResume && <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] border-emerald-300 text-emerald-800 hover:bg-emerald-50" onClick={() => setDialog("pause")} data-testid="journey-resume-btn"><PlayCircle className="w-3 h-3 mr-1" /> Resume</Button>}
          <button type="button" onClick={load} className="h-7 w-7 inline-flex items-center justify-center rounded hover:bg-white/70 text-slate-500" title="Recompute" data-testid="journey-banner-refresh"><RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /></button>
        </div>
      </div>

      {/* Next stage → then the next calendar item: the calendar drives the file */}
      {(showRoute || na) && (
        <div className="mt-2 pt-2 border-t border-slate-200/70 flex flex-wrap items-center gap-x-3 gap-y-1.5" data-testid="journey-banner-next">
          {showRoute && <RouteStatusControl caseRow={caseRow} onChanged={() => { onChanged?.(); load(); }} readOnly={!isLegalMode} />}
          <span className="inline-flex items-center gap-1.5 text-xs text-slate-800" data-testid="journey-banner-next-item">
            <CalendarClock className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-[10px] uppercase tracking-wider text-slate-500">then</span>
            {na ? (
              <>
                <span className="font-medium">{na.label}</span>
                <span className={`tabular-nums ${na.overdue_days > 0 ? "text-red-700 font-semibold" : "text-slate-600"}`}>· {fmt(na.date)}{na.overdue_days > 0 ? ` · ${na.overdue_days}d overdue` : ""}</span>
                {na.owner && <span className="text-slate-500">· {na.owner}</span>}
              </>
            ) : <span className="italic text-slate-400">nothing diarised — add the next calendar item</span>}
          </span>
        </div>
      )}
      {attention.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1" data-testid="journey-banner-attention">
          {attention.map((a) => <li key={a.key} className="text-[11px] text-amber-800" title={a.reason}>⚠ <span className="font-medium">{a.label}</span> — {a.reason}</li>)}
        </ul>
      )}
      {dialog === "pause" && <PauseLegalDialog caseRow={caseRow} journey={j} onClose={() => setDialog(null)} onDone={done} />}
      {dialog === "restructure" && <RestructureDecisionDialog caseRow={caseRow} journey={j} onClose={() => setDialog(null)} onDone={done} />}
    </div>
  );
}
